import {
  signatureDrawnPngMaxBytes,
  type PortalItemSignature,
  type PortalSignatureResponse,
  type SignatureFieldKind,
} from "@ledgerbox/domain"

/* The pure half of the signing screen: what the client sees, when Sign is
   allowed, and the words for each state. The screen renders these; tests pin
   them. */

export type SigningStage =
  | "sign"
  | "others_sign"
  | "signed"
  | "canceled"
  | "changed"

export const signingStage = (signature: PortalSignatureResponse): SigningStage => {
  if (signature.status === "signed") return "signed"
  if (signature.status === "voided") {
    return signature.voidReason === "replaced" ? "changed" : "canceled"
  }
  return signature.signerIsYou ? "sign" : "others_sign"
}

/** The action or state line on the item in the checklist and workbench. */
export const itemSigningLabel = (input: {
  signature: PortalItemSignature
  youAreSigner: boolean
  firmName: string
  /** How the signing day reads, e.g. "September 25". Left out, the line
      names only the signer. */
  formatDay?: (iso: string) => string
}): string => {
  const { signature } = input
  if (signature.status === "signed") {
    // When it was signed belongs with who signed it (R8-U8).
    const day =
      signature.signedAt && input.formatDay
        ? ` on ${input.formatDay(signature.signedAt)}`
        : ""
    return `Signed by ${signature.signerName ?? "your contact"}${day}`
  }
  if (signature.status === "voided") return `${input.firmName} canceled this signing`
  if (input.youAreSigner) return "Read and sign"
  return `${signature.signerName ?? "Another contact"} signs this document`
}

export const fieldPlaceholders: Record<SignatureFieldKind, string> = {
  signature: "Sign here",
  date_signed: "Date",
  full_name: "Your name",
}

export type SignatureInput =
  | { method: "typed"; fullName: string }
  | { method: "drawn"; fullName: string; drawnPng: string | null }

const drawSignatureProblem = "Draw your signature"

/** The line under an empty signature pad. */
export const drawPadHint = "Draw your signature in the box"

/** Why Sign is off, or null when the client can sign. */
export const signProblem = (input: {
  consented: boolean
  signature: SignatureInput
}): string | null => {
  if (!input.consented) return "Agree to sign electronically first"
  if (!input.signature.fullName.trim()) return "Enter your full name"
  if (input.signature.method === "drawn") {
    if (!input.signature.drawnPng) return drawSignatureProblem
    return drawnPngProblem(input.signature.drawnPng)
  }
  return null
}

/** The line under Sign, or null. An empty pad already says to draw, so the
 * dialog does not say it twice. */
export const signNote = (problem: string | null): string | null =>
  problem === drawSignatureProblem ? null : problem

/**
 * What the page's boxes show while the client signs: the typed name, the
 * name and today's date. Nothing while the Sign dialog is closed, so before
 * Sign the boxes read "Sign here", "Your name" and "Date".
 */
export const signPreview = (input: {
  open: boolean
  method: SignatureInput["method"]
  fullName: string
  dateToday: string
}): Partial<Record<SignatureFieldKind, string>> => {
  const name = input.fullName.trim()
  if (!input.open || !name) return {}
  return {
    signature: input.method === "typed" ? name : undefined,
    full_name: name,
    date_signed: input.dateToday,
  }
}

/** The base64 part of a canvas's PNG data URL. */
export const pngDataUrlToBase64 = (dataUrl: string): string =>
  dataUrl.replace(/^data:image\/png;base64,/, "")

/** A drawing the API will refuse, named before it is sent. */
export const drawnPngProblem = (base64: string): string | null => {
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0
  const bytes = (base64.length * 3) / 4 - padding
  return bytes > signatureDrawnPngMaxBytes
    ? "The drawing is too large. Select Clear and draw it again."
    : null
}

/** What to tell the client when Sign fails, by the API's error code. */
export const signErrorMessage = (code: string, fallback: string, firmName: string): string => {
  switch (code) {
    case "signature_document_changed":
      return "This document changed. Read the new copy before you sign."
    case "signature_canceled":
      return `${firmName} canceled this signing`
    case "consent_required":
      return "Agree to sign electronically first"
    case "not_the_signer":
      return "Another contact signs this document"
    case "name_needs_drawn_signature":
      return "Your name has letters the typed signature cannot show. Select Draw instead."
    default:
      return fallback
  }
}

/** A page's width on screen: the column, less its gutters, never wider than
 * the page at a comfortable reading size. */
export const pageDisplayWidth = (columnWidth: number): number =>
  Math.max(240, Math.min(816, Math.floor(columnWidth)))

/** A field's box from pdf.js's viewport rectangle, in CSS pixels. */
export const viewportBox = (rect: readonly number[]) => {
  const [x1 = 0, y1 = 0, x2 = 0, y2 = 0] = rect
  return {
    left: Math.min(x1, x2),
    top: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
  }
}

/** A tap target at least 44px on each side, centred on the drawn box. */
export const tapTarget = (box: ReturnType<typeof viewportBox>, minimum = 44) => {
  const width = Math.max(box.width, minimum)
  const height = Math.max(box.height, minimum)
  return {
    left: box.left - (width - box.width) / 2,
    top: box.top - (height - box.height) / 2,
    width,
    height,
  }
}
